/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

import { clsx } from '@accelint/design-foundation/lib/utils';
import CloseIcon from '@accelint/icons/cancel';
import PinIcon from '@accelint/icons/pin';
import {
  type DockviewApi,
  DockviewReact,
  type DockviewReadyEvent,
  type IDockviewPanelProps,
} from 'dockview-react';
import {
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { Button } from '../button';
import { Divider } from '../divider';
import { Icon } from '../icon';
import { FloatingCardContext, useFloatingCard } from './context';
import styles from './styles.module.css';
import { createHeaderAdapter, type FloatingCardHeaderProps } from './utils';
import type { UniqueId } from '@accelint/core/utility/uuid';
import type {
  FloatingCardContextValue,
  FloatingCardProviderProps,
} from './types';

/**
 * Internal component that registers a DOM ref for a card container.
 *
 * Used by the floating card engine to mount portal targets. Also manages
 * the data-pinned attribute on the resize container to control drag handle visibility.
 *
 * @param props - Dockview panel props containing the card ID.
 */
function FloatingCardContainer(props: Readonly<IDockviewPanelProps>) {
  const { addRef, isPinned, subscribeToPinState } = useFloatingCard();
  const cardId = props.api.id as UniqueId;

  const pinned = useSyncExternalStore(
    subscribeToPinState,
    () => isPinned(cardId),
    () => false,
  );

  const cardRef = useRef<HTMLDivElement | null>(null);

  const refCallback = useCallback(
    (ref: HTMLDivElement | null) => {
      cardRef.current = ref;
      addRef(cardId, ref);
    },
    [addRef, cardId],
  );

  // Toggle a data-pinned attribute on the ancestor .dv-resize-container
  // so CSS can disable pointer-events on the drag handle.
  useEffect(() => {
    const el = cardRef.current;
    const container = el?.closest<HTMLElement>('.dv-resize-container');

    if (!container) {
      return;
    }

    if (pinned) {
      container.dataset.pinned = '';
    } else {
      delete container.dataset.pinned;
    }
  }, [pinned]);

  return <div className={styles.cardContent} ref={refCallback} />;
}

/**
 * Default left header showing an optional custom icon and the card's title.
 */
function DefaultLeftHeader({
  title,
  icon,
  id,
}: Readonly<FloatingCardHeaderProps>) {
  return (
    <div className={styles.headerSide}>
      {icon ? <Icon size='small'>{icon}</Icon> : null}
      {title && title !== id ? (
        <div className={styles.headerTitleContainer}>
          <div className={styles.headerTitle}>{title}</div>
        </div>
      ) : null}
    </div>
  );
}

/** Stable no-op subscribe for useSyncExternalStore when no pin subscription is available. */
const noopSubscribe = () => () => undefined;

/**
 * Default right header showing optional action buttons and an always-present close button.
 *
 * @remarks Handles rendering of 'divider' and 'pin' action types specially.
 */
function DefaultRightHeader({
  closeGroup,
  headerActions,
  id,
  togglePinCard,
  isPinned,
  subscribeToPinState,
}: Readonly<FloatingCardHeaderProps>) {
  const pinned = useSyncExternalStore(
    subscribeToPinState ?? noopSubscribe,
    () => (id && isPinned ? isPinned(id as UniqueId) : false),
    () => false,
  );

  return (
    <div className={styles.headerSide}>
      {headerActions?.map((action, index) => {
        if (action === 'divider') {
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: Using index as key is acceptable here because the order of actions is unlikely to change.
            <Divider key={`${id}-divider-${index}`} orientation='vertical' />
          );
        }
        if (action === 'pin') {
          return (
            <Button
              className={styles.pinButton}
              // biome-ignore lint/suspicious/noArrayIndexKey: Using index as key is acceptable here because the order of actions is unlikely to change.
              key={`${id}-pin-${index}`}
              variant='icon'
              size='small'
              color={pinned ? 'accent' : undefined}
              aria-label='Pin'
              aria-pressed={pinned}
              onClick={() => {
                if (id && togglePinCard) {
                  togglePinCard(id as UniqueId);
                }
              }}
            >
              <Icon>
                <PinIcon />
              </Icon>
            </Button>
          );
        }
        return (
          <Button
            key={`${id}-${
              // biome-ignore lint/suspicious/noArrayIndexKey: Using index as key is acceptable here because the order of actions is unlikely to change.
              index
            }`}
            variant='icon'
            size='small'
            onClick={action.onClick}
          >
            <Icon>{action.icon}</Icon>
          </Button>
        );
      })}
      <Button variant='icon' size='small' onClick={closeGroup}>
        <Icon>
          <CloseIcon />
        </Icon>
      </Button>
    </div>
  );
}

const components: Record<string, FunctionComponent<IDockviewPanelProps>> = {
  default: FloatingCardContainer,
};

/**
 * Provides a context and layout area for floating cards within the application.
 *
 * Wraps children with floating card context and renders a Dockview instance to manage
 * docking, dragging, and layout of floating cards.
 *
 * @param props - The props for the FloatingCardProvider component.
 * @param props.children - Child components rendered inside the floating card provider.
 * @param props.icon - Optional icon rendered in all card headers (static or factory).
 * @param props.headerActions - Optional action buttons for card headers (static or factory).
 * @param props.className - Additional CSS class names for styling.
 * @returns The FloatingCardProvider component that manages floating card layout and context.
 *
 * @remarks
 * - Manages registration and cleanup of floating card DOM references
 * - Exposes closeCard, togglePinCard, and isPinned via context
 * - Automatically cleans up refs when cards are removed via any path (drag close, group close, API close)
 * - Cards are bounded within the viewport
 *
 * @example
 * ```tsx
 * <FloatingCardProvider
 *   icon={<AppLogo />}
 *   headerActions={[
 *     { icon: <SettingsIcon />, onClick: openSettings },
 *     'divider',
 *     'pin'
 *   ]}
 * >
 *   <FloatingCard id="panel-1" title="My Panel">
 *     <div>Panel content</div>
 *   </FloatingCard>
 * </FloatingCardProvider>
 * ```
 */
export function FloatingCardProvider({
  children,
  icon,
  headerActions,
  initialPinned,
  className,
}: FloatingCardProviderProps) {
  const [api, setApi] = useState<DockviewApi | null>(null);
  const [cards, setCards] = useState<Record<UniqueId, HTMLDivElement>>({});
  const pinnedCardsRef = useRef<Set<UniqueId>>(new Set(initialPinned));
  const pinListenersRef = useRef<Set<() => void>>(new Set());

  const closeCard = useCallback(
    (id: UniqueId) => {
      api?.getPanel(id)?.api.close();
    },
    [api],
  );

  const togglePinCard = useCallback((id: UniqueId) => {
    const next = new Set(pinnedCardsRef.current);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    pinnedCardsRef.current = next;
    for (const listener of pinListenersRef.current) {
      listener();
    }
  }, []);

  const isPinned = useCallback(
    (id: UniqueId) => pinnedCardsRef.current.has(id),
    [],
  );

  const subscribeToPinState = useCallback((callback: () => void) => {
    pinListenersRef.current.add(callback);
    return () => {
      pinListenersRef.current.delete(callback);
    };
  }, []);

  const removeRef = useCallback((view: UniqueId) => {
    setCards((prev) => {
      const newCards = { ...prev };
      delete newCards[view];
      return newCards;
    });
    if (pinnedCardsRef.current.has(view)) {
      const next = new Set(pinnedCardsRef.current);
      next.delete(view);
      pinnedCardsRef.current = next;
      for (const listener of pinListenersRef.current) {
        listener();
      }
    }
  }, []);

  const addRef = useCallback((id: UniqueId, ref: HTMLDivElement | null) => {
    if (ref) {
      setCards((prev) => {
        if (prev[id]) {
          return prev;
        }
        return { ...prev, [id]: ref };
      });
    }
  }, []);

  // Clean up stale refs when cards are removed via any path
  // (drag close, group close, API close, etc.)
  useEffect(() => {
    if (!api) {
      return;
    }

    const disposable = api.onDidRemovePanel((event) => {
      removeRef(event.id as UniqueId);
    });

    return () => {
      disposable.dispose();
    };
  }, [api, removeRef]);

  const contextValue = useMemo<FloatingCardContextValue>(
    () => ({
      cards,
      addRef,
      removeRef,
      closeCard,
      togglePinCard,
      isPinned,
      subscribeToPinState,
      api,
    }),
    [
      cards,
      closeCard,
      removeRef,
      api,
      addRef,
      togglePinCard,
      isPinned,
      subscribeToPinState,
    ],
  );

  const leftAdapter = useMemo(
    () =>
      createHeaderAdapter(DefaultLeftHeader, {
        icon,
      }),
    [icon],
  );

  const rightAdapter = useMemo(
    () =>
      createHeaderAdapter(DefaultRightHeader, {
        headerActions,
        togglePinCard,
        isPinned,
        subscribeToPinState,
      }),
    [headerActions, togglePinCard, isPinned, subscribeToPinState],
  );

  const theme = useMemo(
    () => ({
      name: 'accelint',
      className: styles.floatingCardProvider ?? '',
    }),
    [],
  );

  const onReady = useCallback((event: DockviewReadyEvent) => {
    setApi(event.api);
  }, []);

  return (
    <FloatingCardContext.Provider value={contextValue}>
      <div className={clsx(styles.providerRoot, className)}>
        <DockviewReact
          locked
          floatingGroupBounds={'boundedWithinViewport'}
          components={components}
          prefixHeaderActionsComponent={leftAdapter}
          rightHeaderActionsComponent={rightAdapter}
          onReady={onReady}
          theme={theme}
        />
        {children}
      </div>
    </FloatingCardContext.Provider>
  );
}
